import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Printer, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const LOGO_URL = "/agha_logo_sm.png";

function fmtMoney(n) {
  return Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

// Number to Arabic words (simplified, up to millions) for invoice
const ARABIC_ONES = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة", "عشرة",
  "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"];
const ARABIC_TENS = ["", "", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"];

function lessThanHundred(n) {
  if (n < 20) return ARABIC_ONES[n];
  const ones = n % 10;
  const tens = Math.floor(n / 10);
  if (ones === 0) return ARABIC_TENS[tens];
  return `${ARABIC_ONES[ones]} و${ARABIC_TENS[tens]}`;
}

function lessThanThousand(n) {
  if (n < 100) return lessThanHundred(n);
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  let hundredsWord = "";
  if (hundreds === 1) hundredsWord = "مائة";
  else if (hundreds === 2) hundredsWord = "مائتان";
  else hundredsWord = `${ARABIC_ONES[hundreds]} مائة`;
  return rest === 0 ? hundredsWord : `${hundredsWord} و${lessThanHundred(rest)}`;
}

function numberToArabicWords(num) {
  num = Math.floor(Number(num) || 0);
  if (num === 0) return "صفر";
  if (num < 1000) return lessThanThousand(num);
  if (num < 1000000) {
    const thousands = Math.floor(num / 1000);
    const rest = num % 1000;
    let thWord = "";
    if (thousands === 1) thWord = "ألف";
    else if (thousands === 2) thWord = "ألفان";
    else if (thousands <= 10) thWord = `${lessThanThousand(thousands)} آلاف`;
    else thWord = `${lessThanThousand(thousands)} ألف`;
    return rest === 0 ? thWord : `${thWord} و${lessThanThousand(rest)}`;
  }
  const millions = Math.floor(num / 1000000);
  const rest = num % 1000000;
  let mWord = "";
  if (millions === 1) mWord = "مليون";
  else if (millions === 2) mWord = "مليونان";
  else if (millions <= 10) mWord = `${lessThanThousand(millions)} ملايين`;
  else mWord = `${lessThanThousand(millions)} مليون`;
  if (rest === 0) return mWord;
  if (rest < 1000) return `${mWord} و${lessThanThousand(rest)}`;
  const thousands = Math.floor(rest / 1000);
  const rest2 = rest % 1000;
  let thWord = "";
  if (thousands === 1) thWord = "ألف";
  else if (thousands === 2) thWord = "ألفان";
  else if (thousands <= 10) thWord = `${lessThanThousand(thousands)} آلاف`;
  else thWord = `${lessThanThousand(thousands)} ألف`;
  return rest2 === 0 ? `${mWord} و${thWord}` : `${mWord} و${thWord} و${lessThanThousand(rest2)}`;
}

export default function ReceiptPage() {
  const { customerId, paymentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: c } = await api.get(`/customers/${customerId}`);
        setData(c);
        // Auto-trigger print after render
        setTimeout(() => window.print(), 800);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, paymentId]);

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">جاري التحميل...</div>;
  }

  const payments = data.payments || [];
  const idx = payments.findIndex((p) => p.id === paymentId);
  if (idx === -1) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-zinc-600">القسط غير موجود</p>
        <Button onClick={() => navigate("/")}>العودة</Button>
      </div>
    );
  }
  const payment = payments[idx];
  // Compute remaining after THIS payment
  const paidUpToHere = payments.slice(0, idx + 1).reduce((s, p) => s + Number(p.amount || 0), 0);
  const remainingAfter = Number(data.total_amount || 0) - paidUpToHere;
  const amountInWords = numberToArabicWords(payment.amount);

  const today = new Date().toLocaleDateString("en-GB");

  return (
    <div className="min-h-screen bg-zinc-100 py-6 print:bg-white print:py-0">
      {/* Toolbar (hidden when printing) */}
      <div className="max-w-3xl mx-auto px-4 flex items-center justify-between mb-4 no-print">
        <Button variant="outline" onClick={() => window.close() || navigate(`/customer/${customerId}`)}>
          <X className="h-4 w-4 ms-1" />
          إغلاق
        </Button>
        <Button onClick={() => window.print()} className="bg-teal-700 hover:bg-teal-800 font-bold">
          <Printer className="h-4 w-4 ms-1" />
          طباعة
        </Button>
      </div>

      {/* Receipt */}
      <div className="max-w-3xl mx-auto bg-white shadow-lg print:shadow-none border border-zinc-200 print:border-0 receipt-paper">
        {/* Two copies for cut */}
        <ReceiptBody
          data={data}
          payment={payment}
          remainingAfter={remainingAfter}
          paidUpToHere={paidUpToHere}
          amountInWords={amountInWords}
          today={today}
          copyLabel="نسخة المحل"
        />
        <div className="border-t-2 border-dashed border-zinc-400 my-2 print:my-1 relative">
          <span className="absolute -top-3 right-1/2 translate-x-1/2 bg-white px-3 text-[10px] text-zinc-500 font-bold tracking-wider">✂  CUT HERE  ✂</span>
        </div>
        <ReceiptBody
          data={data}
          payment={payment}
          remainingAfter={remainingAfter}
          paidUpToHere={paidUpToHere}
          amountInWords={amountInWords}
          today={today}
          copyLabel="نسخة الزبون"
        />
      </div>

      <style>{`
        @media print {
          @page { margin: 8mm; size: A4; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .receipt-paper { box-shadow: none !important; border: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function ReceiptBody({ data, payment, remainingAfter, paidUpToHere, amountInWords, today, copyLabel }) {
  return (
    <div className="p-8 print:p-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b-2 border-amber-600 pb-5 mb-5">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Agha Phone" className="h-20 w-20 object-contain" />
          <div>
            <div className="text-2xl font-display font-extrabold text-teal-800 leading-tight">آغا فون</div>
            <div className="text-xs font-latin font-semibold text-amber-700 tracking-widest">AGHA PHONE</div>
            <div className="text-[10px] text-zinc-500 font-latin tracking-wide mt-1">MOBILE · ACCESSORIES</div>
          </div>
        </div>
        <div className="text-left">
          <div className="inline-block px-3 py-1 bg-amber-100 text-amber-800 rounded-md text-[10px] font-bold tracking-widest">
            {copyLabel}
          </div>
          <div className="mt-2 text-xs text-zinc-600 font-latin">Date: <span className="font-bold text-zinc-900 num">{today}</span></div>
        </div>
      </div>

      {/* Title */}
      <div className="text-center mb-6">
        <div className="inline-block bg-teal-800 text-white px-8 py-2 rounded-full text-base font-display font-bold tracking-wide">
          إيصال تسديد قسط
        </div>
        <div className="mt-2 text-xs text-zinc-500 font-latin tracking-wider">PAYMENT RECEIPT</div>
      </div>

      {/* Receipt number + customer */}
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="border-2 border-zinc-200 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-latin font-semibold mb-1">رقم الإيصال</div>
          <div className="text-xl font-bold text-zinc-900 num">#{String(payment.serial_number).padStart(3, "0")}</div>
        </div>
        <div className="border-2 border-zinc-200 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-latin font-semibold mb-1">تاريخ التسديد</div>
          <div className="text-xl font-bold text-zinc-900 num">{payment.payment_date}</div>
        </div>
      </div>

      {/* Customer */}
      <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 mb-5">
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-latin font-semibold">اسم الزبون</div>
            <div className="text-base font-bold text-zinc-900">{data.full_name}</div>
          </div>
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-latin font-semibold">رقم الهاتف</div>
            <div className="text-base font-bold text-zinc-900 num" style={{ direction: "ltr" }}>{data.phone || "—"}</div>
          </div>
          {data.address && (
            <div className="col-span-2">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-latin font-semibold">العنوان</div>
              <div className="text-sm text-zinc-700">{data.address}</div>
            </div>
          )}
        </div>
      </div>

      {/* Amount */}
      <div className="bg-gradient-to-l from-teal-50 to-amber-50 border-2 border-teal-700 rounded-lg p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-zinc-700">المبلغ المسدد</div>
          <div className="text-4xl font-extrabold text-teal-800 num">
            {fmtMoney(payment.amount)}
            <span className="text-base text-amber-700 ms-2">د.ع</span>
          </div>
        </div>
        <div className="text-xs text-zinc-600 border-t border-teal-200 pt-2">
          <span className="font-bold">المبلغ كتابةً:</span> {amountInWords} دينار عراقي فقط لا غير.
        </div>
      </div>

      {/* Summary */}
      <table className="w-full text-sm mb-6 border border-zinc-200 rounded-lg overflow-hidden">
        <tbody>
          <tr className="bg-zinc-50 border-b border-zinc-200">
            <td className="px-3 py-2 text-zinc-600 text-xs uppercase tracking-wider font-latin font-semibold">المبلغ الكلي</td>
            <td className="px-3 py-2 text-left font-bold text-zinc-900 num">{fmtMoney(data.total_amount)} د.ع</td>
          </tr>
          <tr className="border-b border-zinc-200">
            <td className="px-3 py-2 text-zinc-600 text-xs uppercase tracking-wider font-latin font-semibold">مجموع التسديدات حتى الآن</td>
            <td className="px-3 py-2 text-left font-bold text-emerald-600 num">{fmtMoney(paidUpToHere)} د.ع</td>
          </tr>
          <tr className="bg-orange-50">
            <td className="px-3 py-2 text-zinc-700 text-xs uppercase tracking-wider font-latin font-bold">المبلغ المتبقي بعد هذا التسديد</td>
            <td className="px-3 py-2 text-left font-extrabold text-orange-700 text-base num">{fmtMoney(remainingAfter)} د.ع</td>
          </tr>
        </tbody>
      </table>

      {payment.note && (
        <div className="mb-5 text-xs text-zinc-600">
          <span className="font-bold">ملاحظة:</span> {payment.note}
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-3 border-t border-zinc-200 text-center text-[10px] text-zinc-500 font-latin tracking-wider">
        شكراً لتعاملكم معنا — AGHA PHONE · MOBILE & ACCESSORIES
      </div>
    </div>
  );
}
